import type { QueryClient, QueryKey } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { trackEvent } from '@/lib/telemetry'

export const ACTIVATE_MUTATION_KEY = ['activate-quote']

// Root segment for mutations that provably cannot touch quote / quotedetail /
// cpqfields / milestone / pricing data — activation has nothing to gain by
// waiting on them (sending the proposal email, saving a template).
// This is a deliberate BLOCKLIST, not an allowlist: a mutation only stops
// blocking activation once it is positively classified as non-edit and tagged
// with this root, so anything keyless or unclassified keeps blocking.
export const NON_QUOTE_EDIT_MUTATION_KEY_ROOT = 'non-quote-edit'

// True when activation is about to block on an unfinished save — callers tell
// the user instead of leaving the button looking inert.
// Offline-paused mutations sit in 'pending' indefinitely — waiting on them
// would wedge activation after any network blip, so count only in-flight ones.
export function hasPendingUserMutations(queryClient: QueryClient) {
  return queryClient
    .getMutationCache()
    .getAll()
    .some((m) => {
      if (m.state.status !== 'pending' || m.state.isPaused) return false
      const root = m.options.mutationKey?.[0]
      return root !== ACTIVATE_MUTATION_KEY[0] && root !== NON_QUOTE_EDIT_MUTATION_KEY_ROOT
    })
}

// Wait for every other in-flight mutation to settle before activating.
// Clicking Activate blurs a focused input, which commits a blur-PATCH into the
// mutation cache before this runs — a disabled-button gate can't re-render in
// time to catch that, so the activation itself must wait.
// Resolves true when it had to wait (callers re-run the plugin flush so the
// late edits' derived rows converge before totals are locked in).
export function awaitMutationQuiescence(queryClient: QueryClient, timeoutMs = 15_000): Promise<boolean> {
  const cache = queryClient.getMutationCache()
  if (!hasPendingUserMutations(queryClient)) return Promise.resolve(false)
  return new Promise<boolean>((resolve, reject) => {
    const timeout = setTimeout(() => {
      unsubscribe()
      reject(new Error('Your edits are still saving. Please try activating again in a moment.'))
    }, timeoutMs)
    const unsubscribe = cache.subscribe(() => {
      if (!hasPendingUserMutations(queryClient)) {
        clearTimeout(timeout)
        unsubscribe()
        resolve(true)
      }
    })
  })
}

type ActivationQuoteVersion = {
  quoteid: string
  statecode?: number | null
  statuscode?: number | null
  dsy_approvalsignaturedatetime?: string | null
}

// Re-read the quote before a version-checked call (activate, milestone
// replacements): an async plugin (payment-summary recalculation after a
// milestone write) bumps its row version with no client edit, so the cached
// etag can be stale and the server's If-Match check 412s. A status or
// signature change since the click still refuses.
// Fetched directly, not through the query: a failed read fails only this
// activation, and an older in-flight refetch is never reused.
export async function readLatestQuote<T extends ActivationQuoteVersion>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  fetchQuote: () => Promise<T>,
  clicked: T
): Promise<T> {
  const latest = await fetchQuote()
  queryClient.setQueryData(queryKey, latest)
  if (latest.quoteid !== clicked.quoteid) {
    throw new Error('Quote changed while calculations were syncing. Refresh the quote and try again.')
  }
  if (
    latest.statecode !== clicked.statecode ||
    latest.statuscode !== clicked.statuscode ||
    latest.dsy_approvalsignaturedatetime !== clicked.dsy_approvalsignaturedatetime
  ) {
    throw new Error('Quote status changed while saves were finishing. Refresh and review the quote before trying again.')
  }
  return latest
}

// The recalculation can still land between the re-read and the server's
// version check (activation's own flush writes milestones). A 412 is retried once on
// a fresh read; that read refuses if status or signature changed, so the retry
// is the same action, never a different one.
// Each milestone write queues its own recalculation, so the retry waits for a
// burst to settle. Tracked, so the remaining 412 rate stays measurable.
export async function retryOnceOnStaleVersion<R>(
  operation: string,
  attempt: () => Promise<R>,
  retry: () => Promise<R>,
  delayMs = 2500
): Promise<R> {
  try {
    return await attempt()
  } catch (error) {
    if (!isAxiosError(error) || error.response?.status !== 412) throw error
    trackEvent('stale-version-retry', { operation })
    await new Promise((resolve) => setTimeout(resolve, delayMs))
    return await retry()
  }
}

// A lifecycle request fenced by the quote's etag (close as lost, back to
// draft), sent on a fresh read and retried once on a version-only 412.
// Opening a Draft writes its totals and system jobs touch the row, so the
// cached etag goes stale with no status change; a status change still refuses.
export async function runOnLatestQuoteVersion<T extends ActivationQuoteVersion, R>(
  operation: string,
  queryClient: QueryClient,
  queryKey: QueryKey,
  fetchQuote: () => Promise<T>,
  clicked: T,
  request: (latest: T) => Promise<R>,
  delayMs?: number
): Promise<R> {
  const send = async () => request(await readLatestQuote(queryClient, queryKey, fetchQuote, clicked))
  return retryOnceOnStaleVersion(operation, send, send, delayMs)
}
